import React, { useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';

const TimeBasedReportChart = ({ timeBasedReport, activeChartType, setActiveChartType }) => {
  const chartRef = useRef(null);
  
  if (!timeBasedReport || timeBasedReport.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-6xl mb-4 text-gray-300">📈</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Accuracy Trends Over Time</h3>
        <p className="text-gray-600">No data available</p>
        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or date range</p>
      </div>
    );
  }

  const sortedData = [...timeBasedReport].sort((a, b) => {
    if (a.intervalType === 'week') {
      return a.timeInterval.localeCompare(b.timeInterval);
    }
    return new Date(a.timeInterval) - new Date(b.timeInterval);
  });
  
  const formatLabels = (timeInterval, intervalType) => {
    if (intervalType === 'week') {
      const [year, week] = timeInterval.split('-W');
      return `Week ${week}, ${year}`;
    }
    return timeInterval;
  };
  
  const chartData = {
    labels: sortedData.map(item => formatLabels(item.timeInterval, item.intervalType)),
    datasets: [
      {
        label: 'Accuracy %',
        data: sortedData.map(item => item.overallAccuracy),
        backgroundColor: activeChartType === 'line' ? 'transparent' : '#dc2626',
        borderColor: '#dc2626',
        borderWidth: activeChartType === 'line' ? 3 : 0,
        tension: activeChartType === 'line' ? 0.1 : undefined,
        pointRadius: activeChartType === 'line' ? 6 : undefined,
        pointBackgroundColor: '#dc2626',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        fill: false,
        borderRadius: activeChartType === 'bar' ? 4 : undefined,
        borderSkipped: activeChartType === 'bar' ? false : undefined,
        barPercentage: 0.7,
        categoryPercentage: 0.8,
      },
    ],
  };
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: '#f3f4f6',
          borderColor: '#e5e7eb'
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
            family: 'system-ui'
          }
        },
        title: {
          display: true,
          text: 'Accuracy (%)',
          color: '#374151',
          font: {
            size: 13,
            weight: '600',
            family: 'system-ui'
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          color: '#6b7280',
          font: {
            size: 11,
            family: 'system-ui'
          }
        }
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: '#1f2937',
        titleColor: '#ffffff',
        bodyColor: '#e5e7eb',
        borderColor: '#374151',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const dataPoint = sortedData[context.dataIndex];
            const skuCount = dataPoint.locationDetails?.reduce((sum, loc) => sum + loc.skuCount, 0) || 0;
            return [
              `Overall Accuracy: ${context.parsed.y.toFixed(2)}%`,
              `SKUs Counted: ${skuCount}`,
              `Locations: ${dataPoint.locationDetails?.length || 0}`
            ];
          }
        }
      },
      legend: {
        display: false
      }
    }
  };
  
  return (
    <div className="bg-white p-6">
      <div className="mb-6">
        <div className="flex items-center justify-end">
          <span className="bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200">
            {sortedData.length} Periods
          </span>
        </div>
      </div>
      
      <div className="mb-6">
        <div className="inline-flex bg-gray-100 rounded-lg p-1 border border-gray-200">
          <button 
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeChartType === 'bar' 
                ? 'bg-gray-900 text-white shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveChartType('bar')}
          >
            Bar Chart
          </button>
          <button 
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeChartType === 'line' 
                ? 'bg-gray-900 text-white shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveChartType('line')}
          >
            Line Chart
          </button>
        </div>
      </div>
      
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="h-[400px]">
          {activeChartType === 'bar' ? (
            <Bar 
              ref={chartRef}
              data={chartData} 
              options={options}
            />
          ) : (
            <Line data={chartData} options={options} />
          )}
        </div>
      </div>
      
      {/* Summary stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {Math.max(...sortedData.map(d => d.overallAccuracy)).toFixed(1)}%
          </div>
          <div className="text-xs text-gray-600 mt-1">Highest Period</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {(sortedData.reduce((sum, d) => sum + d.overallAccuracy, 0) / sortedData.length).toFixed(1)}%
          </div>
          <div className="text-xs text-gray-600 mt-1">Average</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {Math.min(...sortedData.map(d => d.overallAccuracy)).toFixed(1)}%
          </div>
          <div className="text-xs text-gray-600 mt-1">Lowest Period</div>
        </div>
      </div>
    </div>
  );
};

export default TimeBasedReportChart;